import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  getValidationResolver,
  SubmitHandler,
  useForm,
} from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { Accordion } from '@/components';
import { Topbar } from '@/modules/core/components';
import ModuleWrapper from '@/modules/plan/ModuleWrapper';
import globalTheme from '@/styles/theme';

import { FieldLevelAccessSetupProperties, IFieldLevelAccessSetup, NO_ACCESS_ROLE_ID } from '../role.types';

import FieldLevelAccessSetupConfirmModal from './FieldLevelAccessSetupConfirmModal/FieldLevelAccessSetupConfirmModal';
import BasicProperties from './FieldLevelAccessSetupForm/BasicProperties';
import FieldLevelAccessAppliedToSection from './FieldLevelAccessSetupForm/BasicProperties/FieldLevelAccessAppliedToSection';
import FieldLevelAccessProperties from './FieldLevelAccessSetupForm/FieldLevelAccessProperties/FieldLevelAccessProperties';
import PropertiesCopyModal from './FieldLevelAccessSetupForm/FieldLevelAccessProperties/PropertiesCopyModal';
import { useDefaultPropertiesValues } from './hooks/useDefaultPropertiesValues';
import FieldLevelAccessSetupFinal from './FieldLevelAccessSetupFinal';
import FieldLevelAccessSetupSidebar from './FieldLevelAccessSetupSidebar';
import { useFieldLevelAccessSetupStore } from './stores';

interface Props {
  id?: string;
  isEditMode?: boolean;
  isLoading?: boolean;
}
const FieldLevelAccessSetup: React.FC<Props> = ({ isEditMode, isLoading = false, id }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const setState = useFieldLevelAccessSetupStore((state) => state.setState);

  const { preparedProperties, preparedOverridableProperties, propLoading } = useDefaultPropertiesValues();

  const state = useFieldLevelAccessSetupStore((state) => state.state);
  const errorFields = useFieldLevelAccessSetupStore((state) => state.errorFields);

  const methods = useForm<IFieldLevelAccessSetup>({
    defaultValues: state,
    resolver: getValidationResolver({
      name: VALIDATORS.REQUIRED_STRING.test({
        test: (value) => {
          const fieldValue = errorFields?.name?.value;
          if (!fieldValue) {
            return true;
          }
          return value !== fieldValue;
        },
        message: errorFields?.name?.message,
      }),
    }),
  });
  const isInitialSetup = useMemo(() => !state.id, [state.id]);

  const handleApplyProperties = useCallback(
    (properties: FieldLevelAccessSetupProperties) => {
      methods.reset({
        ...state,
        properties: properties.properties,
        overridableProperties: properties.overridableProperties,
      });
    },
    [methods, state]
  );

  useEffect(() => {
    if (errorFields?.name?.value) {
      methods.trigger('name');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFields?.name?.value]);

  useEffect(() => {
    if (!isLoading && state.id && isEditMode) {
      methods.reset(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, state.id]);

  useEffect(() => {
    if (!state.properties) {
      setState({
        properties: preparedProperties,
        overridableProperties: preparedOverridableProperties,
      });
      methods.reset({
        ...state,
        properties: preparedProperties,
        overridableProperties: preparedOverridableProperties,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparedProperties, preparedOverridableProperties]);

  const handleSubmit: SubmitHandler<IFieldLevelAccessSetup> = useCallback(
    (data) => {
      const preparedData = {
        properties: state.properties,
        overridableProperties: state.overridableProperties,
        ...data,
      };
      setState(preparedData);
      setShowConfirm(true);
    },
    [setState, state.overridableProperties, state.properties]
  );

  return (
    <>
      <FieldLevelAccessSetupConfirmModal
        isEditMode={!!id}
        visible={showConfirm}
        isDirty={methods.formState.isDirty}
        onSetVisible={setShowConfirm}
      />
      <FormProvider {...methods} key={state.id}>
        <Box fill direction="row" background="module">
          <FieldLevelAccessSetupSidebar isLoading={isLoading} />

          <Box
            direction="column"
            id="role_setup"
            data-testid="form-preloader"
            style={{ position: 'relative' }}
            fill
            overflow="auto"
            background="canvas"
          >
            <Box direction="column">
              <div>
                <Topbar isSticky>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <AppButton width="100px" type="submit">
                      Save
                    </AppButton>
                  </form>
                </Topbar>

                <Box pad={{ horizontal: '40px', vertical: 's' }} width={globalTheme.defaultContentWidth}>
                  <Box direction="row" align="center" justify="between">
                    <Accordion />
                    {state.id !== NO_ACCESS_ROLE_ID && (
                      <PropertiesCopyModal onApply={handleApplyProperties} />
                    )}
                  </Box>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <ModuleWrapper
                      testId="basic-properties"
                      anchorId="basic_properties"
                      title="Basic Properties"
                      expandless
                    >
                      <BasicProperties isLoading={isLoading} defaultValues={state} isEditMode={isEditMode} />

                      {!isInitialSetup && (
                        <FieldLevelAccessAppliedToSection isLoading={isLoading} roles={state.roles} />
                      )}
                    </ModuleWrapper>

                    <ModuleWrapper
                      testId="field-level-access-normal-properties"
                      anchorId="normal_organization_properties"
                      title="Normal Organization Properties"
                      expandless
                    >
                      <FieldLevelAccessProperties
                        title="Normal Organization Properties"
                        testId="normal_organization_properties"
                        isEditMode={isEditMode}
                        defaultValues={state.properties}
                        isLoading={isLoading || propLoading}
                      />
                    </ModuleWrapper>

                    <ModuleWrapper
                      testId="field-level-access-overridable-properties"
                      anchorId="overridable_organization_properties"
                      title="Overridable Organization Properties"
                      expandless
                    >
                      <FieldLevelAccessProperties
                        isOverridableProperties
                        title="Overridable Organization Properties"
                        testId="overridable_organization_properties"
                        isEditMode={isEditMode}
                        defaultValues={state.overridableProperties}
                        isLoading={isLoading || propLoading}
                      />
                    </ModuleWrapper>

                    <ModuleWrapper testId="final_setup" anchorId="final_setup" title="Final Setup">
                      <FieldLevelAccessSetupFinal />
                    </ModuleWrapper>
                  </form>
                </Box>
              </div>
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  );
};
export default React.memo(FieldLevelAccessSetup);
